interface ExportCustomReportFormPdfOptions {
  element: HTMLElement;
  reportName: string;
}

const safeFileName = (value: string) => (
  value
    .trim()
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    || 'custom-report'
);

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Datagrids render as wide horizontal tables that overflow A4 and get clipped. For the PDF we stack each
// row vertically (see printRendererShellSx) as "Column label: value" — that needs each cell to carry its
// column header, so copy the thead labels onto every body cell's data-label before capture.
const stampDatagridLabels = (element: HTMLElement) => {
  element.querySelectorAll('.formio-component-datagrid table').forEach((table) => {
    const headers = Array.from(table.querySelectorAll('thead th')).map((th) => th.textContent?.trim() ?? '');
    table.querySelectorAll('tbody tr').forEach((row) => {
      Array.from(row.children).forEach((cell, index) => {
        (cell as HTMLElement).setAttribute('data-label', headers[index] ?? '');
      });
    });
  });
};

const waitForImages = async (element: HTMLElement) => {
  const images = Array.from(element.querySelectorAll('img'));

  await Promise.all(images.map((image) => {
    if (image.complete) return Promise.resolve();

    return new Promise<void>((resolve) => {
      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', () => resolve(), { once: true });
    });
  }));
};

const dataUriToBytes = (dataUri: string) => {
  const [, base64 = ''] = dataUri.split(',');
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const sliceCanvas = (canvas: HTMLCanvasElement, sourceY: number, sourceHeight: number) => {
  const pageCanvas = document.createElement('canvas');
  pageCanvas.width = canvas.width;
  pageCanvas.height = sourceHeight;

  const context = pageCanvas.getContext('2d');
  if (!context) throw new Error('Unable to prepare PDF page.');

  context.drawImage(
    canvas,
    0,
    sourceY,
    canvas.width,
    sourceHeight,
    0,
    0,
    canvas.width,
    sourceHeight,
  );

  return pageCanvas;
};

// Renders the captured form element to a PDF and returns the bytes as a Blob (no download). Used both
// by the download wrapper below and by the stope-note generation flow, which uploads these bytes so a
// user's template becomes the stored stope note.
export const buildCustomReportFormPdfBlob = async (element: HTMLElement): Promise<Blob> => {
  await waitForImages(element);
  stampDatagridLabels(element);

  const [{ default: html2canvas }, pdfLib] = await Promise.all([
    import('html2canvas'),
    import('pdf-lib'),
  ]);

  // The colour toolbars are fill-time controls — hide them just for the capture so the PDF shows only
  // the coloured text.
  const toolbars = Array.from(element.querySelectorAll<HTMLElement>('.crt-toolbar'));
  const previousDisplay = toolbars.map((toolbar) => toolbar.style.display);
  toolbars.forEach((toolbar) => {
    const toolbarEl = toolbar;
    toolbarEl.style.display = 'none';
  });

  // Capture at the element's own box width (not scrollWidth): a wide table or off-screen layout can inflate
  // scrollWidth, which then scales the whole page down to a tiny column. Forcing the render window to the
  // box width keeps the content filling the page. Fixed scale:2 for a crisp result regardless of DPR.
  const captureWidth = Math.round(element.getBoundingClientRect().width) || element.offsetWidth || element.scrollWidth;
  let canvas: HTMLCanvasElement;
  try {
    canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      height: element.scrollHeight,
      logging: false,
      scale: 2,
      useCORS: true,
      width: captureWidth,
      windowHeight: element.scrollHeight,
      windowWidth: captureWidth,
    });
  } finally {
    toolbars.forEach((toolbar, index) => {
      const toolbarEl = toolbar;
      toolbarEl.style.display = previousDisplay[index];
    });
  }

  if (!canvas.width || !canvas.height) {
    throw new Error('Unable to render the custom form.');
  }

  const pdfDoc = await pdfLib.PDFDocument.create();
  const pageWidth = 595.28;
  const pageHeight = 841.89;
  const margin = 24;
  const contentWidth = pageWidth - margin * 2;
  const contentHeight = pageHeight - margin * 2;
  const imageScale = contentWidth / canvas.width;
  const canvasPageHeight = Math.max(1, Math.floor(contentHeight / imageScale));

  for (let sourceY = 0; sourceY < canvas.height; sourceY += canvasPageHeight) {
    const sourceHeight = Math.min(canvasPageHeight, canvas.height - sourceY);
    const pageCanvas = sliceCanvas(canvas, sourceY, sourceHeight);
    // eslint-disable-next-line no-await-in-loop -- pages must be embedded and appended in order
    const image = await pdfDoc.embedPng(dataUriToBytes(pageCanvas.toDataURL('image/png')));
    const imageHeight = sourceHeight * imageScale;
    const page = pdfDoc.addPage([pageWidth, pageHeight]);

    page.drawImage(image, {
      x: margin,
      y: pageHeight - margin - imageHeight,
      width: contentWidth,
      height: imageHeight,
    });
  }

  const pdfBytes = await pdfDoc.save();
  // Ensure we pass an ArrayBuffer (not a SharedArrayBuffer-backed view) to Blob to satisfy
  // the BlobPart type expectations across environments.
  const pdfArrayBuffer = (pdfBytes instanceof Uint8Array)
    ? pdfBytes.slice().buffer
    : new Uint8Array(pdfBytes).buffer;

  return new Blob([pdfArrayBuffer], { type: 'application/pdf' });
};

const exportCustomReportFormPdf = async ({
  element,
  reportName,
}: ExportCustomReportFormPdfOptions) => {
  const blob = await buildCustomReportFormPdfBlob(element);
  downloadBlob(blob, `${safeFileName(reportName)}-filled.pdf`);
};

export default exportCustomReportFormPdf;
